import * as React from 'react'
import { browserHistory } from 'react-router'
import { toJS } from 'mobx'
import { inject, observer } from 'mobx-react'
import { ChromePicker } from 'react-color'
import {
  CreateBaseCategory,
  CreateCategory,
  ComplianceCategoryIdMapping,
  IComplianceCategoryContract,
  ICategoryContract,
  PermissionNames,
} from '@getgreenline/homi-shared'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { ChildCategory } from './ChildCategory'
import { Modal, ModalHeader, ModalBody, Popover, PopoverBody } from 'reactstrap'
import './AddCategoryModal.scss'
import { CurrentUserStore } from '../../../../stores/CurrentUserStore'
import { SaveButton } from '../../../../components/Button/SaveButton'
import { FormError } from '../../../../components/FormError'
import { ReactSingleSelect, ISelectOption } from '../../../../components/ReactSelect'
import { NoPermissionsResult } from '../../../../components/NoPermissionsResult'
import Helmet from 'react-helmet'
import { THEME_COLORS } from '@getgreenline/shared'
import { Select } from 'antd-v4'
import { BlazeSelectFilter } from '../../../../components/CustomSelect/BlazeSelectFilter'
import { BlazeAlert } from '../../../../components/BlazeAlert/BlazeAlert'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  currentUserStore?: CurrentUserStore
  location?: any
}

interface State {
  isEdit: boolean
  isSaving: boolean
  errorMessage?: string
  colorsExpanded: boolean
  requiresCompliance: boolean
}

@inject('currentCompanyStore', 'currentUserStore')
@observer
export class AddCategoryModal extends React.Component<Props, State> {
  category = new CreateCategory()
  company = this.props.currentCompanyStore!.company!

  constructor(props: Props) {
    super(props)
    this.state = {
      isEdit: false,
      isSaving: false,
      errorMessage: undefined,
      colorsExpanded: false,
      requiresCompliance: true,
    }
  }

  componentDidMount() {
    const editCategoryId = this.props.location.query.categoryId

    if (editCategoryId) {
      const categories = this.props.currentCompanyStore!.productStore!.categories!
      const matchingCategory = categories.find((category) => category.id === editCategoryId)

      if (matchingCategory) {
        this.assignParentCategory(matchingCategory)
        this.assignChildCategories()
      }
    }
  }

  assignParentCategory = (matchingCategory: ICategoryContract) => {
    matchingCategory.complianceCategoryId !== undefined &&
    matchingCategory.complianceCategoryId !== null
      ? this.setState({ isEdit: true })
      : this.setState({ isEdit: true, requiresCompliance: false })
    Object.assign(this.category, matchingCategory)
  }

  assignChildCategories = () => {
    this.category.childCategories = this.naturallySortedChildCategories.map((childCategory) => {
      const childCategoryInstance = new CreateBaseCategory()
      Object.assign(childCategoryInstance, childCategory)
      return childCategoryInstance
    })
  }

  save() {
    if (!this.state.requiresCompliance) {
      this.category.complianceCategoryId = null
    }
    if (!this.category.name) {
      return this.setState({ errorMessage: 'Please input the category title' })
    }

    if (!this.category.complianceCategoryId && this.state.requiresCompliance) {
      return this.setState({ errorMessage: 'You must assign a compliance category' })
    }

    const childCategoryWithNoName = this.category.childCategories.find((cc) => !cc.name)
    if (childCategoryWithNoName) {
      return this.setState({ errorMessage: 'Every subcategory must have a title' })
    }

    if (this.state.isEdit) {
      const editCategoryId = this.props.location.query.categoryId

      this.props
        .currentCompanyStore!.productStore!.updateCategory(editCategoryId, toJS(this.category))
        .then(() => {
          this.onHide()
        })
        .catch((error: any) => {
          this.setState({
            errorMessage: error.response.data.message || 'There was an error editing your category',
          })
        })
    } else {
      this.props
        .currentCompanyStore!.productStore!.addCategory(toJS(this.category))
        .then(() => {
          this.onHide()
        })
        .catch((error: any) => {
          this.setState({
            errorMessage: error.response.data.message || 'There was an error adding your category',
          })
        })
    }
  }

  onHide() {
    const currentPath = window.location.pathname
    const backUrl = currentPath.replace('/add-category', '')
    browserHistory.push(backUrl)
  }

  get categoryOptions() {
    const complianceCategories =
      this.props.currentCompanyStore!.productStore!.complianceCategories || []
    const companyProvince = this.props.currentCompanyStore!.company!.province

    const filteredComplianceCategories: IComplianceCategoryContract[] = complianceCategories.filter(
      ({ id: complianceId }) => {
        // No province, get all categories
        if (!companyProvince) {
          return true
        }

        // No-one should use the original oil category now
        if (complianceId === ComplianceCategoryIdMapping.OIL) {
          return false
        }

        // Only Ontario should see the AGCO_ACCESSORY compliance category
        if (
          companyProvince !== 'ON' &&
          complianceId === ComplianceCategoryIdMapping.AGCO_ACCESSORY
        ) {
          return false
        }

        return true
      },
    )

    const categoryOptions: ISelectOption[] = filteredComplianceCategories.map((cc) => {
      return {
        label: cc.name,
        value: cc.id,
      }
    })

    return categoryOptions
  }

  get canEditCategories() {
    return this.props.currentUserStore!.hasPermissionAtEntity(
      PermissionNames.CAN_EDIT_CATEGORIES,
      this.company.id,
    )
  }

  get buttonSelectedStyle() {
    return this.state.requiresCompliance
      ? 'button-selected bg-light border-primary'
      : 'button-not-selected'
  }

  get buttonNotSelectedStyle() {
    return this.state.requiresCompliance
      ? 'button-not-selected'
      : 'button-selected bg-light border-primary'
  }

  get naturallySortedChildCategories() {
    return this.category.childCategories.sort((a, b) => {
      return a.name.localeCompare(b.name, 'en', {
        numeric: true,
        sensitivity: 'base',
      })
    })
  }

  get currentComplianceCategory(): ISelectOption | undefined {
    return this.categoryOptions.find(
      (option) => option.value === this.category.complianceCategoryId,
    )
  }

  render() {
    const { requiresCompliance, isEdit, colorsExpanded, errorMessage, isSaving } = this.state

    return (
      <Modal isOpen={true} toggle={() => this.onHide()}>
        <Helmet>
          <title>Products | Categories | Edit</title>
          <meta name='page-loaded' content='true' />
        </Helmet>
        <ModalHeader toggle={() => this.onHide()}>
          <span>{isEdit ? 'Edit category' : 'Add a category'}</span>
        </ModalHeader>
        <ModalBody>
          {this.canEditCategories ? (
            <form>
              <div className='d-flex'>
                <div className='form-group d-flex'>
                  <div
                    id='colors-popover'
                    className='d-flex color-picker'
                    onClick={() => this.setState({ colorsExpanded: !colorsExpanded })}
                  >
                    <i
                      className='fa fa-square fa-3x'
                      style={{ color: this.category.colorHex || 'gray' }}
                    />
                  </div>
                </div>
                <div className='col-md-8'>
                  <div className='w-100'>Select colour</div>
                  <small>Your POS will display categories in this color</small>
                </div>
              </div>

              <div className='row mt-1'>
                <div className='col-md-12'>
                  <label>Category name</label>
                  <input
                    className='form-control'
                    defaultValue='Category name'
                    value={this.category.name}
                    onChange={(e) => {
                      this.category.name = e.target.value
                      this.setState({ colorsExpanded: false })
                    }}
                  />
                </div>
              </div>

              <Popover
                placement='bottom'
                isOpen={colorsExpanded}
                target={'colors-popover'}
                toggle={() => this.setState({ colorsExpanded: !colorsExpanded })}
              >
                <PopoverBody>
                  <ChromePicker
                    color={this.category.colorHex || '#808080'} // default gray color
                    disableAlpha={true}
                    onChangeComplete={(newColor) => (this.category.colorHex = newColor.hex)}
                  />
                </PopoverBody>
              </Popover>

              <div className='d-flex form-group mt-4 flex-row-space'>
                <div
                  className={`d-flex btn select-button flex-column center ${this.buttonSelectedStyle}`}
                  onClick={() => {
                    this.setState({ requiresCompliance: true })
                  }}
                >
                  <b>Contains cannabis</b>
                  {requiresCompliance && (
                    <span className='text-muted'>Requires a compliance category</span>
                  )}
                </div>
                <div
                  className={`d-flex btn select-button flex-column center ${this.buttonNotSelectedStyle}`}
                  onClick={() => {
                    this.setState({ requiresCompliance: false })
                  }}
                >
                  <div className='row'>
                    <b>Does not contain cannabis</b>
                  </div>
                  {!requiresCompliance && (
                    <div className='row text-muted'>Will not be tracked in compliance reports</div>
                  )}
                </div>
              </div>

              {requiresCompliance && (
                <div className='mt-4'>
                  <div className='form-group'>
                    <label>Compliance category</label>
                    <span className='ml-2 italic' style={{ color: THEME_COLORS.ACTIONS.dangerAlt }}>
                      Required
                    </span>

                    <BlazeSelectFilter<ISelectOption>
                      placeholder='Select...'
                      value={this.currentComplianceCategory?.value}
                      options={this.categoryOptions}
                      onChange={(value?: ISelectOption['value']) => {
                        this.category.complianceCategoryId = value
                      }}
                      allowClear
                    />
                  </div>
                </div>
              )}

              {this.category.childCategories.length > 0 && (
                <div className='mt-4 d-flex'>
                  <label>Subcategories</label>
                </div>
              )}

              {this.category.childCategories.map((childCategory, index) => {
                return (
                  <div key={index} className='child-input'>
                    <ChildCategory category={this.category} childCategoryIndex={index} />
                  </div>
                )
              })}
              {
                <div className='mt-4 mb-5'>
                  <button
                    className='btn btn-light'
                    onClick={(e) => {
                      e.preventDefault()
                      const childCategory = new CreateBaseCategory()
                      childCategory.colorHex = this.category.colorHex
                      this.category.childCategories.push(childCategory)
                    }}
                  >
                    <span className='fa fa-plus mr-2'></span>
                    Add subcategory
                  </button>
                </div>
              }
              <SaveButton title='Save' onClick={() => this.save()} isSaving={isSaving} />
              {errorMessage && (
                <div className='mt-2'>
                  <BlazeAlert type='error' showIcon>
                    {errorMessage}
                  </BlazeAlert>
                </div>
              )}
            </form>
          ) : (
            <NoPermissionsResult permissionName={PermissionNames.CAN_EDIT_CATEGORIES} />
          )}
        </ModalBody>
      </Modal>
    )
  }
}
